import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Box, Typography, Grid2, Link, IconButton } from "@mui/material";
import CloseButton from "../../../../Components/CloseButton";
import FullScreenDialog from "../../../../Components/FullScreenDialog";
import StateOfUseDrillDownTable from "./StateOfUseDrillDownTable";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import TextWithCopy from "../../../../Features/Table/GenericDraggableTable/Renderers/columnTypes/TextWithCopy";

const LEVEL_1 = "level1";
const LEVEL_2 = "level2";

const ModalHeader = ({
  currentLevel,
  activeTitle,
  level2Title,
  bannerComponent,
  handleBackToLevel1,
  onClose,
}) => (
  <Grid2 container justifyContent="space-between" alignItems="flex-start" py={2} gap={2}>

    <Grid2
      container
      justifyContent="space-between"
      alignItems="center"
      width="100%"
      sx={{ borderBottom: "1px solid #E6E6E6", px: 3 }}
    >
      <Grid2 container flexDirection="column" gap={1} sx={{ pb: 2 }}>
        <Typography variant="fw700s20c000000" sx={{ lineHeight: "28px" }}>
          Drill Down:
        </Typography>
        <Typography variant="fw500s16c8F8F8F" sx={{ lineHeight: "28px" }}>
          {activeTitle}
        </Typography>
      </Grid2>
      <Grid2 container justifyContent="center" alignItems="center">
        <CloseButton onClick={onClose} />
      </Grid2>
    </Grid2>

    <Grid2 container width="100%" flexDirection="column" gap={2}>
      {bannerComponent && <Box sx={{ px: 3, pb: 2 }}>{bannerComponent}</Box>}

      {currentLevel === LEVEL_1 && (
        <Typography variant="fw500s18lh24c000000" sx={{ pl: 3 }}>
          Summary
        </Typography>
      )}

      {currentLevel === LEVEL_2 && (
        <Grid2 container gap={0.5} alignItems="center" sx={{ pl: 3 }}>
          <IconButton size="small" onClick={handleBackToLevel1}>
            <ArrowBackIosNewIcon sx={{ fontSize: 12 }} />
          </IconButton>
          <Typography variant="fw500s18lh24c000000">{level2Title}</Typography>
        </Grid2>
      )}
    </Grid2>

  </Grid2>
);

const StateOfUseDrillDownModal = ({
  open,
  onClose,
  title,
  selectedDataPoint,
  tableConfigs,
  getTableData,
  applyTableAction,
  getCSVFileName,
  pageSize = 10,
  level2TableConfigs,
  level2GetTableData,
  level2ApplyTableAction,
  level2GetCSVFileName,
  level2Title,
  bannerComponent,
}) => {
  const [currentLevel, setCurrentLevel] = useState(LEVEL_1);
  const [level2SelectedRecord, setLevel2SelectedRecord] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    if (!open) {
      setCurrentLevel(LEVEL_1);
      setLevel2SelectedRecord(null);
      setIsTransitioning(false);
    }
  }, [open]);

  const handleLevel2Click = useCallback(
    (record) => {
      if (level2TableConfigs && level2GetTableData) {
        setIsTransitioning(true);
        setLevel2SelectedRecord(record);
        setCurrentLevel(LEVEL_2);
        setTimeout(() => setIsTransitioning(false), 100);
      }
    },
    [level2TableConfigs, level2GetTableData]
  );

  const handleBackToLevel1 = useCallback(() => {
    setCurrentLevel(LEVEL_1);
    setLevel2SelectedRecord(null);
    setIsTransitioning(false);
  }, []);

  const customRenderers = useMemo(
    () => ({
      "vehicle-license-plate": (record) => {
        const isActive = record?.status === "active";
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                flexShrink: 0,
                backgroundColor: isActive ? "#2DB55D" : "#9E9E9E",
              }}
            />
            <TextWithCopy
              text={record?.licensePlateNumber}
              type="Bus Number"
              textStyle={{ color: "#6D72F6" }}
            />
          </Box>
        );
      },

      "details-link": (record) => {
        if (!record) return null;
        if (currentLevel === LEVEL_1 && level2TableConfigs && level2GetTableData) {
          return (
            <Link
              component="button"
              onClick={() => handleLevel2Click(record)}
              sx={{
                color: "#6D72F6",
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              Details
              <ArrowForwardIosIcon sx={{ fontSize: 12 }} />
            </Link>
          );
        }
        return null;
      },
    }),
    [currentLevel, level2TableConfigs, level2GetTableData, handleLevel2Click]
  );

  const level1Args = useMemo(
    () => ({
      tableConfigs,
      getTableData,
      applyTableAction,
      getCSVFileName,
      selectedDataPoint,
      title: typeof title === "function" ? title(selectedDataPoint) : title || "Chart Details",
      showColumnSettings: false,
    }),
    [tableConfigs, getTableData, applyTableAction, getCSVFileName, selectedDataPoint, title]
  );

  const level2Args = useMemo(() => {
    if (!level2SelectedRecord) return null;
    return {
      tableConfigs: level2TableConfigs,
      getTableData: level2GetTableData,
      applyTableAction: level2ApplyTableAction
        ? (keys, action, selectAll, params = {}) =>
            level2ApplyTableAction(keys, action, selectAll, params, level2SelectedRecord)
        : undefined,
      getCSVFileName: level2GetCSVFileName
        ? () => level2GetCSVFileName(level2SelectedRecord)
        : undefined,
      selectedDataPoint: level2SelectedRecord,
      showColumnSettings: true,
      title: level2Title
        ? typeof level2Title === "function"
          ? level2Title(level2SelectedRecord)
          : level2Title
        : "Chart Details",
    };
  }, [
    level2TableConfigs,
    level2GetTableData,
    level2ApplyTableAction,
    level2GetCSVFileName,
    level2Title,
    level2SelectedRecord,
  ]);

  const activeArgs = useMemo(
    () => (currentLevel === LEVEL_1 ? level1Args : level2Args || level1Args),
    [currentLevel, level1Args, level2Args]
  );

  return (
    <FullScreenDialog
      open={open}
      handleClose={onClose}
      containerStyles={{ bgcolor: "#FAFAFA", overflowY: "hidden" }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          bgcolor: "#FAFAFA",
          height: "100vh",
          overflow: "hidden",
        }}
      >
        <ModalHeader
          currentLevel={currentLevel}
          activeTitle={level1Args?.title}
          level2Title={level2Args?.title}
          handleBackToLevel1={handleBackToLevel1}
          bannerComponent={bannerComponent}
          onClose={onClose}
        />

        <Box sx={{ flex: 1, overflow: "auto", px: 3, pb: 2, minHeight: 0 }}>
          <StateOfUseDrillDownTable
            {...activeArgs}
            pageSize={pageSize}
            customRenderers={customRenderers}
            isOpen={open}
          />
        </Box>
      </Box>
    </FullScreenDialog>
  );
};

export default StateOfUseDrillDownModal;
